import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Award, MessageSquare, Menu, X, ClipboardList } from 'lucide-react';
import skillsbridgeLogo from '../img/skillsbridge.png';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import { getAuthToken } from '../../services/api';
import axios from 'axios';
import ProfileModal from '../../Components/Profile/ProfileModal';
import './vStyles/VolunteerLayout.css';

interface VolunteerProfile {
  id: number;
  firstname: string;
  surname: string;
  email: string;
}

export default function VolunteerLayout() {
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isOrangeTheme, setIsOrangeTheme] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profile, setProfile] = useState<VolunteerProfile | null>(null);
  const location = useLocation();
  const { volunteerId } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

  // Update active view based on current route
  useEffect(() => {
    if (location.pathname === '/volunteer') setActiveView('dashboard');
    else if (location.pathname === '/volunteer/badges') setActiveView('badges');
    else if (location.pathname === '/volunteer/my-tasks') setActiveView('my-tasks');
  }, [location]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch volunteer profile
  useEffect(() => {
    const fetchVolunteerProfile = async () => {
      if (!volunteerId) return;
      
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/volunteers/${volunteerId}`, { headers });
        setProfile(response.data as VolunteerProfile);
      } catch (error) {
        console.error('Error fetching volunteer profile:', error);
      }
    };

    fetchVolunteerProfile();
  }, [volunteerId, API_BASE_URL]);

  const getUserInitials = () => {
    const userName = profile ? `${profile.firstname} ${profile.surname}` : "Volunteer User";
    return userName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={`volunteer-container ${sidebarCollapsed && !isMobile ? 'collapsed' : ''} ${isOrangeTheme ? 'orange-theme' : ''}`}>
      {/* Header with logo for mobile */}
      {isMobile && (
        <header className="mobile-header">
          <Link to="/" className="logo-link">
            <img src={skillsbridgeLogo} alt="SkillsBridge" className="logo-image" />
            SkillsBridge
          </Link>
          <div className="mobile-header-controls">
            <button 
              className={`theme-switch ${isOrangeTheme ? 'orange-active' : ''}`}
              onClick={() => setIsOrangeTheme(!isOrangeTheme)}
              aria-label="Switch theme"
            />
            <button 
              onClick={() => setIsProfileModalOpen(true)}
              className="mobile-profile-button"
              aria-label="Open profile menu"
              title="My Profile"
            >
              {getUserInitials()}
            </button>
          </div>
        </header>
      )}

      <ProfileModal 
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Sidebar for desktop */}
      {!isMobile && (
        <aside className="sidebar">
          <div className="sidebar-header">
            <Link to="/" className="logo">
              {sidebarCollapsed ? (
                <img src={skillsbridgeLogo} alt="SkillsBridge" className="logo-image-collapsed" />
              ) : (
                <>
                  <img src={skillsbridgeLogo} alt="SkillsBridge" className="logo-image" />
                  SkillsBridge
                </>
              )}
            </Link>
            <div className="header-controls">
              <button 
                className={`theme-switch ${isOrangeTheme ? 'orange-active' : ''}`}
                onClick={() => setIsOrangeTheme(!isOrangeTheme)}
                aria-label="Switch theme"
              />
              <button 
                className="collapse-btn"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                aria-label="Collapse sidebar"
              >
                {sidebarCollapsed ? <Menu size={16} /> : <X size={16} />}
              </button>
            </div>
          </div>

          <nav>
            <ul>
              <li>
                <Link 
                  to="/volunteer" 
                  className={activeView === 'dashboard' ? 'active' : ''}
                >
                  <LayoutDashboard size={20} className="nav-icon" />
                  <span className="nav-text">Dashboard</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/volunteer/badges" 
                  className={activeView === 'badges' ? 'active' : ''}
                >
                  <Award size={20} className="nav-icon" />
                  <span className="nav-text">Badges</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/volunteer/my-tasks" 
                  className={activeView === 'my-tasks' ? 'active' : ''}
                >
                  <ClipboardList size={20} className="nav-icon" />
                  <span className="nav-text">My Tasks</span>
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
      )}

      {/* Main Content */}
      <main className={`content ${isMobile ? 'mobile-content' : ''}`}>
        <Outlet />
      </main>

      {/* Bottom navigation for mobile */}
      {isMobile && (
        <nav className="bottom-nav">
          <Link 
            to="/volunteer" 
            className={`nav-item ${activeView === 'dashboard' ? 'active' : ''}`}
          >
            <LayoutDashboard size={20} className="nav-icon" />
            <span className="nav-label">Dashboard</span>
          </Link>
          <Link 
            to="/volunteer/badges" 
            className={`nav-item ${activeView === 'badges' ? 'active' : ''}`}
          >
            <Award size={20} className="nav-icon" />
            <span className="nav-label">Badges</span>
          </Link>
          <Link 
            to="/volunteer/my-tasks" 
            className={`nav-item ${activeView === 'my-tasks' ? 'active' : ''}`}
          >
            <ClipboardList size={20} className="nav-icon" />
            <span className="nav-label">My Tasks</span>
          </Link>
        </nav>
      )}
    </div>
  );
}